import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  TrendingUp, 
  Eye, 
  Search, 
  Users, 
  Crown,
  ArrowUpRight,
  Calendar,
  Phone,
  Mail,
  Globe,
  Share2
} from "lucide-react";
import { useProfileMetrics } from "@/hooks/useProfileMetrics";

interface UnifiedMetricsProps {
  listingId: string;
  listingUserId: string;
  isPremium: boolean;
  views: number;
}

export const UnifiedMetrics = ({ 
  listingId, 
  listingUserId, 
  isPremium, 
  views 
}: UnifiedMetricsProps) => {
  const { loading, isOwner, isAuthenticated, metrics, navigate } = useProfileMetrics({
    type: 'listing',
    profileId: listingId,
    profileUserId: listingUserId,
    views
  });

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-muted rounded w-2/3"></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="h-20 bg-muted rounded"></div>
              <div className="h-20 bg-muted rounded"></div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Make all metrics public
  // Make all metrics public
  const showShares = true;
  const premiumMultiplier = 5;

  return (
    <Card className="border-primary/20 bg-gradient-to-br from-background to-primary/5">
      <CardContent className="pt-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            <h3 className="font-bold text-lg">Tu Rendimiento</h3>
          </div>
          <Badge variant="secondary" className="text-xs">
            Este mes
          </Badge>
        </div>

        {/* Main Metrics Grid */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          {/* Search Appearances */}
          <div className="bg-background/80 backdrop-blur-sm rounded-lg p-3 border border-primary/10">
            <div className="flex items-center gap-2 mb-1">
              <div className="p-1 bg-primary/10 rounded">
                <Search className="w-3 h-3 text-primary" />
              </div>
              <span className="text-xs text-muted-foreground">Apariciones</span>
            </div>
            <div className="text-xl font-bold text-primary">
              {metrics.searchAppearances}
            </div>
            <p className="text-xs text-muted-foreground">en búsquedas</p>
          </div>

          {/* Profile Views */}
          <div className="bg-background/80 backdrop-blur-sm rounded-lg p-3 border border-secondary/10">
            <div className="flex items-center gap-2 mb-1">
              <div className="p-1 bg-secondary/10 rounded">
                <Eye className="w-3 h-3 text-secondary" />
              </div>
              <span className="text-xs text-muted-foreground">Visitas</span>
            </div>
            <div className="text-xl font-bold text-secondary">
              {metrics.views}
            </div>
            <p className="text-xs text-muted-foreground">al perfil</p>
          </div>

          {/* Unique Visitors */}
          <div className="bg-background/80 backdrop-blur-sm rounded-lg p-3 border border-accent/10">
            <div className="flex items-center gap-2 mb-1">
              <div className="p-1 bg-accent/10 rounded">
                <Users className="w-3 h-3 text-accent" />
              </div>
              <span className="text-xs text-muted-foreground">Únicos</span>
            </div>
            <div className="text-xl font-bold text-accent">
              {metrics.uniqueVisitors}
            </div>
            <p className="text-xs text-muted-foreground">visitantes</p>
          </div>

          {/* Shares */}
          {showShares && (
            <div className="bg-background/80 backdrop-blur-sm rounded-lg p-3 border border-primary/10">
              <div className="flex items-center gap-2 mb-1">
                <div className="p-1 bg-primary/10 rounded">
                  <Share2 className="w-3 h-3 text-primary" />
                </div>
                <span className="text-xs text-muted-foreground">Compartidos</span>
              </div>
              <div className="text-xl font-bold text-primary">
                {metrics.shares}
              </div>
              <p className="text-xs text-muted-foreground">veces</p>
            </div>
          )}

          {/* Contacts total (public) */}
          <div className="bg-background/80 backdrop-blur-sm rounded-lg p-3 border border-primary/10">
            <div className="flex items-center gap-2 mb-1">
              <div className="p-1 bg-primary/10 rounded">
                <Phone className="w-3 h-3 text-primary" />
              </div>
              <span className="text-xs text-muted-foreground">Contactos</span>
            </div>
            <div className="text-xl font-bold text-primary">
              {metrics.contactClicks.total}
            </div>
            <p className="text-xs text-muted-foreground">clics</p>
          </div>
        </div>

        {/* Contact Clicks Detail - Only owner sees breakdown */}
        {isOwner && (
          <div className="space-y-2 mb-4">
            <h4 className="text-sm font-semibold mb-2">Clics de Contacto</h4>
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-muted/50 rounded-lg p-2 text-center">
                <Phone className="w-4 h-4 mx-auto mb-1 text-accent" />
                <div className="text-lg font-bold">{metrics.contactClicks.phone}</div>
                <p className="text-xs text-muted-foreground">Teléfono</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-2 text-center">
                <Mail className="w-4 h-4 mx-auto mb-1 text-primary" />
                <div className="text-lg font-bold">{metrics.contactClicks.email}</div>
                <p className="text-xs text-muted-foreground">Email</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-2 text-center">
                <Globe className="w-4 h-4 mx-auto mb-1 text-secondary" />
                <div className="text-lg font-bold">{metrics.contactClicks.website}</div>
                <p className="text-xs text-muted-foreground">Web</p>
              </div>
            </div>
          </div>
        )}

        {/* Conversion Rate - always visible if views > 0 */}
        {metrics.views > 0 && (
          <div className="bg-muted/50 rounded-lg p-3 mb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground mb-1">
                  Tasa de conversión
                </p>
                <p className="text-lg font-bold">{metrics.conversionRate}%</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground mb-1">
                  Contactos totales
                </p>
                <div className="flex items-center gap-1 text-xs">
                  <ArrowUpRight className="w-3 h-3 text-primary" />
                  <span className="font-semibold">{metrics.contactClicks.total}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Premium Comparison */}
        {!isPremium && isOwner && (
          <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-lg p-4 border border-primary/20">
            <div className="flex items-start gap-3 mb-3">
              <div className="p-2 bg-primary rounded-full">
                <Crown className="w-4 h-4 text-primary-foreground" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-sm mb-2">
                  Potencial con Premium
                </h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Búsquedas</span>
                    <span className="font-bold text-primary">
                      ~{metrics.searchAppearances * premiumMultiplier}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Visitas</span>
                    <span className="font-bold text-primary">
                      ~{metrics.views * premiumMultiplier}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Contactos</span>
                    <span className="font-bold text-primary">
                      ~{(metrics.contactClicks.total * premiumMultiplier) || premiumMultiplier}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
                  Los perfiles premium obtienen <strong className="text-primary">{premiumMultiplier}× más contactos</strong>
                </p>
              </div>
            </div>
            <Button
              onClick={() => navigate("/premium-upgrade")}
              className="w-full bg-gradient-primary"
              size="sm"
            >
              <Crown className="w-4 h-4 mr-2" />
              Actualizar a Premium
            </Button>
          </div>
        )}

        {/* Premium Success */}
        {isPremium && (
          <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-lg p-3 border border-primary/20">
            <div className="flex items-center gap-2 mb-2">
              <Crown className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold">Perfil Premium Activo</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Estás aprovechando al máximo tu visibilidad
            </p>
          </div>
        )}

        {/* Growth Tips */}
        {isOwner && metrics.views < 100 && (
          <div className="mt-4 p-3 bg-muted/30 rounded-lg">
            <p className="text-xs font-semibold mb-2">💡 Tips para crecer:</p>
            <ul className="space-y-1 text-xs text-muted-foreground">
              <li className="flex items-start gap-2">
                <div className="w-1 h-1 rounded-full bg-primary flex-shrink-0 mt-1.5" />
                <span>Completa tu perfil al 100%</span>
              </li>
              <li className="flex items-start gap-2">
                <div className="w-1 h-1 rounded-full bg-primary flex-shrink-0 mt-1.5" />
                <span>Añade fotos de calidad</span>
              </li>
              <li className="flex items-start gap-2">
                <div className="w-1 h-1 rounded-full bg-primary flex-shrink-0 mt-1.5" />
                <span>Publica cupones</span>
              </li>
            </ul>
          </div>
        )}

      </CardContent>
    </Card>
  );
};
